//  filepath: src/LandLordPages/LandlordDashboard.jsx
import React from "react";
import { motion } from "framer-motion";
import { Calendar, BarChart2, DollarSign } from "lucide-react";
import useLandlordDashboardData from "../hooks/useLandlordDashboardData";
import ChartCard from "../components/dashboard/ChartCard";
import YoyChartCard from "../components/dashboard/YoyChartCard";
import FacilityTable from "../components/dashboard/FacilityTable";
import { fmtUSDMaybe, fmtIntMaybe } from "../utils/number";
import UserSideLoader from "../components/UserSideLoader";

export default function LandlordDashboard() {
  const { state, actions } = useLandlordDashboardData();
  const {
    view,
    tab,
    loading,
    revenueChart,
    reservationsChart,
    yoyBars,
    summary,
    allFacilities,
    facility_ids,
    date_from,
    date_to,
    utcToday,
    yesterday,
    scopeLabel,
    facilityRowsMTD,
    facilityRowsYTD,
  } = state;

  if (loading) return <UserSideLoader />;

  const revenueValue = fmtUSDMaybe(summary.revenueCents);
  const revenueChangeSign =
    summary.revenueChange == null ? null : summary.revenueChange > 0 ? "+" : "";
  const revenueChangeText =
    summary.revenueChange == null
      ? "—"
      : `${revenueChangeSign}${summary.revenueChange}%`;
  const revenueChangeColor =
    summary.revenueChange == null
      ? "text-gray-500"
      : summary.revenueChange >= 0
      ? "text-emerald-600"
      : "text-red-600";

  const txValue = fmtIntMaybe(summary.txCount);
  const txChangeSign =
    summary.txChange == null ? null : summary.txChange > 0 ? "+" : "";
  const txChangeText =
    summary.txChange == null ? "—" : `${txChangeSign}${summary.txChange}%`;
  const txChangeColor =
    summary.txChange == null
      ? "text-gray-500"
      : summary.txChange >= 0
      ? "text-emerald-600"
      : "text-red-600";

  const changeSuffix = view === "MTD" ? "vs last month" : "vs last year";

  return (
    <div className="min-h-screen w-full rounded-2xl bg-gradient-to-br from-blue-50 via-white to-blue-100 sm:mt-10 p-4 sm:p-6 lg:p-8">
      <div className="mx-auto max-w-7xl">
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 mb-6">
          <div>
            <h1 className="text-xl sm:text-3xl font-bold text-gray-900 tracking-tight">
              Landlord Dashboard
            </h1>
            <p className="text-sm text-gray-600">
              Aggregated performance of your facilities
            </p>
            <div className="mt-2 flex flex-wrap items-center gap-2">
              <span className="inline-flex items-center gap-2 rounded-full border bg-white px-3 py-1 text-xs text-gray-800 shadow-sm">
                <Calendar className="h-3.5 w-3.5 text-blue-600" />
                {view === "MTD"
                  ? `MTD: ${date_from} → ${date_to}`
                  : `YTD: Jan 1, ${new Date().getUTCFullYear()} → Today`}
              </span>
              <span className="inline-flex items-center gap-2 rounded-full border bg-white px-3 py-1 text-xs text-gray-800 shadow-sm">
                {scopeLabel}
              </span>
            </div>
          </div>
          <div className="flex items-center gap-2 bg-white shadow-sm border rounded-xl p-1 w-full sm:w-auto">
            <button
              onClick={() => actions.setView("MTD")}
              className={`flex-1 sm:flex-none px-3 sm:px-4 py-1.5 rounded-lg text-xs sm:text-sm font-medium transition ${
                view === "MTD"
                  ? "bg-blue-600 text-white shadow"
                  : "text-gray-700 hover:bg-blue-50"
              }`}
            >
              Month-to-Date
            </button>
            <button
              onClick={() => actions.setView("YTD")}
              className={`flex-1 sm:flex-none px-3 sm:px-4 py-1.5 rounded-lg text-xs sm:text-sm font-medium transition ${
                view === "YTD"
                  ? "bg-blue-600 text-white shadow"
                  : "text-gray-700 hover:bg-blue-50"
              }`}
            >
              Year-to-Date
            </button>
          </div>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-3 gap-3 sm:gap-5 mb-8">
          {[
            {
              title: view === "MTD" ? "Revenue (MTD)" : "Revenue (YTD)",
              value: revenueValue,
              icon: <DollarSign className="h-4 w-4 text-blue-600" />,
              change:
                summary.revenueChange == null
                  ? "—"
                  : `${revenueChangeText} ${changeSuffix}`,
              changeColor: revenueChangeColor,
            },
            {
              title:
                view === "MTD" ? "Reservations (MTD)" : "Reservations (YTD)",
              value: txValue,
              icon: <Calendar className="h-4 w-4 text-blue-600" />,
              change:
                summary.txChange == null
                  ? "—"
                  : `${txChangeText} ${changeSuffix}`,
              changeColor: txChangeColor,
            },
            {
              title: "Active Facilities",
              value: String(allFacilities.length || 0),
              icon: <BarChart2 className="h-4 w-4 text-blue-600" />,
              change: "Total assigned",
              changeColor: "text-gray-500",
            },
          ].map((card, i) => (
            <motion.div
              key={i}
              whileHover={{ y: -4 }}
              className="rounded-xl sm:rounded-2xl bg-white shadow-sm border p-3 sm:p-5 flex flex-col gap-1 sm:gap-2"
            >
              <div className="flex items-center gap-2 text-xs sm:text-sm text-gray-600">
                {card.icon} {card.title}
              </div>
              <div className="text-lg sm:text-2xl font-semibold text-gray-900">
                {card.value}
              </div>
              <div className={`text-xs sm:text-sm ${card.changeColor}`}>
                {card.change}
              </div>
            </motion.div>
          ))}
        </div>

        <div className="bg-white rounded-2xl shadow-sm border p-3 sm:p-5">
          <div className="flex border-b mb-3 sm:mb-4 overflow-x-auto">
            {["Revenue", "Reservations"].map((item) => (
              <button
                key={item}
                onClick={() => actions.setTab(item)}
                className={`px-3 sm:px-4 py-2 text-xs sm:text-sm font-medium -mb-px border-b-2 transition whitespace-nowrap ${
                  tab === item
                    ? "border-blue-600 text-blue-700"
                    : "border-transparent text-gray-600 hover:text-blue-700 hover:border-blue-200"
                }`}
              >
                {item}
              </button>
            ))}
          </div>
          {view === "MTD" ? (
            <ChartCard
              tab={tab}
              revenueChart={revenueChart}
              reservationsChart={reservationsChart}
              utcToday={utcToday}
              yesterday={yesterday}
            />
          ) : (
            <YoyChartCard tab={tab} yoyBars={yoyBars} />
          )}
        </div>

        <FacilityTable
          view={view}
          tab={tab}
          facilityRowsMTD={facilityRowsMTD}
          facilityRowsYTD={facilityRowsYTD}
        />
      </div>
    </div>
  );
}
